import { Shield, Target, LineChart, Users, Mail, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

function AboutSection({
  icon: Icon,
  title,
  children
}: {
  icon: any;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white/5 backdrop-blur-lg border border-gray-800 rounded-xl shadow-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-yellow-800/30 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-yellow-300" />
        </div>
        <h2 className="text-xl font-bold text-yellow-300">{title}</h2>
      </div>
      <div className="prose prose-invert prose-p:mt-2 prose-ul:mt-2 prose-h3:mt-4 prose-h3:mb-2 max-w-none">
        {children}
      </div>
    </div>
  );
}

export function AboutPage() {
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About PropViper - Your Trusted Source for Prop Firm Reviews",
    "description": "PropViper helps traders navigate the world of proprietary trading firms by providing in-depth reviews, unbiased comparisons, and expert insights.",
    "mainEntity": {
      "@type": "Organization",
      "name": "PropViper",
      "description": "Expert prop trading firm reviews and comparisons platform",
      "url": "https://propviper.com",
      "sameAs": [
        "https://x.com/thepropviper"
      ]
    }
  };

  return (
    <>
      <Helmet>
        <title>About PropViper - Your Trusted Source for Prop Firm Reviews</title>
        <meta
          name="description"
          content="Learn about PropViper's mission to help traders find the best prop trading firms through detailed reviews, comparisons, and expert insights."
        />
        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-950 text-yellow-100 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <div className="text-center mb-12">
              <h1 className="text-4xl sm:text-5xl font-extrabold mb-3 text-yellow-400">About PropViper</h1>
              <p className="text-base sm:text-lg text-yellow-200">
                Your Trusted Source for Prop Firm Reviews & Insights
              </p>
            </div>

            <div className="space-y-10">
              <AboutSection icon={Target} title="Our Mission">
                <p>
                  At PropViper, we help traders navigate the world of proprietary trading firms by providing
                  in-depth reviews, unbiased comparisons, and expert insights. Whether you're a seasoned
                  trader or just starting, our platform empowers you to make informed decisions and find
                  the right prop firm for your trading journey.
                </p>
              </AboutSection>

              <AboutSection icon={Shield} title="Why Trust PropViper?">
                <h3>Unbiased & Transparent</h3>
                <p>
                  Unlike other platforms, we don't push hidden agendas. Our reviews are based on real trader feedback via Trustpilot,
                  comprehensive research on each firm's conditions, and fair & balanced comparisons.
                </p>

                <h3>Data-Driven Insights</h3>
                <p>
                  We analyze key factors such as funding amounts, profit splits, challenge rules, payout reliability,
                  platform compatibility, and support quality.
                </p>
              </AboutSection>

              <AboutSection icon={LineChart} title="What We Offer">
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { title: 'Detailed Reviews', desc: 'Comprehensive analysis of trading conditions, platforms, and support.' },
                    { title: 'Side-by-Side Comparisons', desc: 'Easy-to-understand comparisons of different prop firms.' },
                    { title: 'Educational Content', desc: 'Trading tips, evaluation guides, and risk management insights.' },
                    { title: 'Community Insights', desc: 'Real experiences from verified traders.' },
                  ].map(({ title, desc }) => (
                    <div key={title} className="bg-gray-800/40 p-4 rounded-lg border border-gray-700">
                      <h3 className="font-bold text-yellow-200 mb-1">{title}</h3>
                      <p className="text-sm text-yellow-100">{desc}</p>
                    </div>
                  ))}
                </div>
              </AboutSection>

              <AboutSection icon={Users} title="Community-Driven">
                <p>
                  We believe traders deserve transparency in the prop trading industry. We actively update reviews based on user experiences,
                  monitor market changes, share success stories, and provide a platform for honest feedback.
                </p>
              </AboutSection>

              <AboutSection icon={Star} title="How We Make Money">
                <p>
                  To keep PropViper free, we may earn a small commission when traders sign up through our links. This never affects our
                  reviews or recommendations, and we maintain 100% editorial independence. All sponsored content is clearly marked.
                </p>
              </AboutSection>

              <AboutSection icon={Mail} title="Get in Touch">
                <p>
                  Have questions, feedback, or want us to review a specific firm? Contact us at{' '}
                  <a href="mailto:support@propviper.com" className="text-yellow-300 underline hover:text-yellow-400">
                    support@propviper.com
                  </a>
                </p>
              </AboutSection>

              {/* CTA */}
              <div className="bg-yellow-500/10 border border-yellow-400/20 rounded-xl shadow-lg p-6 text-center">
                <h2 className="text-xl font-bold text-yellow-300 mb-3">Ready to Find Your Perfect Prop Firm?</h2>
                <p className="text-yellow-200 mb-4">
                  Compare the top prop firms and start your funded trading journey today.
                </p>
                <Link
                  to="/compare"
                  className="inline-flex items-center bg-yellow-400 hover:bg-yellow-300 text-black px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Compare Top Firms
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
